import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageSwitcher from './LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import SocialActorAssessment from './SocialActorAssessment';
import SocialActorAnalytics from './SocialActorAnalytics';
import SocialActorMapView from './SocialActorMapView';
import SocialActorQRCode from './SocialActorQRCode';
import NearbyOrganizations from './NearbyOrganizations';
import CollaborationRequests from './CollaborationRequests';
import CSVImportDialog from './CSVImportDialog';
import ScoreBreakdownModal from './ScoreBreakdownModal';
import { 
  Plus,
  Edit,
  Eye,
  CheckCircle,
  XCircle,
  Building2,
  MapPin,
  Globe,
  Mail,
  Phone,
  Award,
  FileText,
  Upload,
  Trash2,
  ArrowLeft,
  Info,
  BarChart3,
  Search,
  TrendingUp,
  SlidersHorizontal,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  X,
  Filter,
  Download,
  GitCompare,
  History,
  Tag,
  Clock,
  User,
  Users,
  CheckSquare,
  Square,
  PieChart,
  Map,
  QrCode,
  Handshake,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = BACKEND_URL;

const SocialActorRegistry = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedActor, setSelectedActor] = useState(null);
  const [editingActor, setEditingActor] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCertification, setFilterCertification] = useState('all');
  const [filterOrgType, setFilterOrgType] = useState('all');
  const [filterSDG, setFilterSDG] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Analytics Dashboard State
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Map View State
  const [showMapView, setShowMapView] = useState(false);
  
  // QR Code State
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeActor, setQRCodeActor] = useState(null);
  
  // Comparison Tool States
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  
  // Activity History States
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [activityHistory, setActivityHistory] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // CSV Import State
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Score Breakdown Modal State
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_type: 'social_enterprise',
    legal_status: '',
    legal_form: '',
    tax_id: '',
    registration_number: '',
    year_established: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    country: 'Greece',
    social_mission: '',
    primary_goals: '',
    target_beneficiaries: '',
    sdg_alignment: [],
    // Financial Information (Step 2)
    annual_revenue: '',
    funding_sources: [],
    // Workforce Details (Step 3)
    total_employees: '',
    volunteers: '',
    members: '',
    beneficiaries_served: '',
    // Privacy Settings
    is_public: true,
    hide_financial_data: false,
    hide_contact_info: false
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [editUploadedFiles, setEditUploadedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  useEffect(() => {
    fetchActors();
  }, [filterStatus, filterCertification]);

  const fetchActors = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/social-actors`;
      const params = new URLSearchParams();
      
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCertification !== 'all') params.append('certification_level', filterCertification);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setActors(response.data);
    } catch (error) {
      console.error('Failed to fetch social actors:', error);
      toast.error('Failed to load social actors');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setUploadedFiles(prev => [...prev, {
          filename: file.name,
          file_type: file.type,
          size: file.size,
          content_base64: base64,
          category: 'registration_document',
          document_name: '',  // User will fill this
          document_description: ''  // User will fill this
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateFileMetadata = (index, field, value) => {
    setUploadedFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateActor = async (e) => {
    e.preventDefault();
    
    // Validate document names
    if (uploadedFiles.length > 0) {
      const missingNames = uploadedFiles.filter(f => !f.document_name.trim());
      if (missingNames.length > 0) {
        toast.error('Please provide a name for all uploaded documents');
        return;
      }
    }
    
    try {
      const actorData = {
        ...formData,
        year_established: formData.year_established ? parseInt(formData.year_established) : null,
        primary_goals: formData.primary_goals.split(',').map(g => g.trim()).filter(g => g),
        sdg_alignment: formData.sdg_alignment.map(s => parseInt(s)),
        // Financial Information → financial_indicators bucket
        financial_indicators: {
          annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
          funding_sources: formData.funding_sources || [],
        },
        // Workforce Details → operational_indicators bucket
        operational_indicators: {
          total_employees: formData.total_employees ? parseInt(formData.total_employees) : null,
          volunteers: formData.volunteers ? parseInt(formData.volunteers) : null,
          members: formData.members ? parseInt(formData.members) : null,
          beneficiaries_served: formData.beneficiaries_served ? parseInt(formData.beneficiaries_served) : null,
        },
        // Privacy Settings
        is_public: !!formData.is_public,
        hide_financial_data: !!formData.hide_financial_data,
        hide_contact_info: !!formData.hide_contact_info,
      };
      // Drop fields the backend create model doesn't accept directly
      delete actorData.annual_revenue;
      delete actorData.funding_sources;
      delete actorData.total_employees;
      delete actorData.volunteers;
      delete actorData.members;
      delete actorData.beneficiaries_served;
      
      const response = await axios.post(`${API}/api/social-actors`, actorData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const actorId = response.data.id;
      
      // Upload files if any
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            await axios.post(`${API}/api/social-actors/${actorId}/documents`, file, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
          } catch (fileError) {
            console.error('Failed to upload file:', fileError);
            toast.error(`Failed to upload ${file.document_name || file.filename}`);
          }
        }
        toast.success(`Profile created with ${uploadedFiles.length} document(s)!`);
      } else {
        toast.success('Social actor profile created successfully!');
      }
      
      setShowCreateDialog(false);
      resetForm();
      setUploadedFiles([]);
      fetchActors();
    } catch (error) {
      console.error('Failed to create social actor:', error);
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handleSubmitForApproval = async (actorId) => {
    try {
      await axios.post(`${API}/api/social-actors/${actorId}/submit`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Profile submitted for approval!');
      fetchActors();
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleReviewActor = async (actorId, action, certLevel = null) => {
    try {
      const params = new URLSearchParams({ action });
      if (certLevel) params.append('certification_level', certLevel);
      
      await axios.post(`${API}/api/social-actors/${actorId}/review?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(`Profile ${action}d successfully!`);
      fetchActors();
    } catch (error) {
      console.error('Failed to review:', error);
      toast.error(error.response?.data?.detail || 'Failed to review profile');
    }
  };

  // Helper function to calculate profile completeness
  const calculateCompleteness = (actor) => {
    let totalFields = 0;
    let filledFields = 0;

    // Priority 1 Fields (20 points each, 6 fields = 120 total)
    const priority1Fields = [
      'organization_name', 'organization_type', 'legal_status',
      'legal_form', 'tax_id', 'email', 'social_mission'
    ];
    
    priority1Fields.forEach(field => {
      totalFields += 20;
      if (actor[field] && actor[field] !== '') filledFields += 20;
    });

    // Priority 2 Assessment Fields (10 points total if any assessment data exists)
    if (actor.organizational_indicators || actor.impact_indicators || actor.investment_readiness) {
      totalFields += 10;
      const hasAssessment = 
        (actor.organizational_indicators && Object.keys(actor.organizational_indicators).length > 0) ||
        (actor.impact_indicators && Object.keys(actor.impact_indicators).length > 0) ||
        (actor.investment_readiness && Object.keys(actor.investment_readiness).length > 0);
      if (hasAssessment) filledFields += 10;
    }

    // Documents (10 points if at least one document)
    totalFields += 10;
    if (actor.documents && actor.documents.length > 0) filledFields += 10;

    const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
    return { percentage, filledFields, totalFields };
  };

  // Helper function to get enhanced certification badge
  const getCertificationBadge = (level) => {
    if (!level) return null;
    
    const badges = {
      level_3_excellence: {
        label: 'Level A - Excellence',
        color: 'bg-gradient-to-r from-yellow-400 to-amber-500',
        textColor: 'text-white',
        icon: '🏆'
      },
      level_2_commitment: {
        label: 'Level B - Commitment',
        color: 'bg-gradient-to-r from-blue-400 to-blue-600',
        textColor: 'text-white',
        icon: '⭐'
      },
      level_1_basic: {
        label: 'Level C - Basic',
        color: 'bg-gradient-to-r from-green-400 to-green-600',
        textColor: 'text-white',
        icon: '✓'
      }
    };

    const badge = badges[level] || badges.level_1_basic;
    
    return (
      <Badge className={`${badge.color} ${badge.textColor} font-semibold px-3 py-1`}>
        {badge.icon} {badge.label}
      </Badge>
    );
  };

  // Get unique cities from actors for the city filter dropdown
  const uniqueCities = [...new Set(actors.filter(a => a.city).map(a => a.city))].sort();

  // SDG options for filter
  const sdgOptions = Array.from({ length: 17 }, (_, i) => i + 1);

  // Organization type options
  const orgTypeOptions = [
    { value: 'social_enterprise', label: 'Social Enterprise' },
    { value: 'non_profit', label: 'Non-Profit' },
    { value: 'cooperative', label: 'Cooperative' },
    { value: 'ngo', label: 'NGO' },
    { value: 'other', label: 'Other' }
  ];

  // Sorting options
  const sortOptions = [
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'date_newest', label: 'Newest First' },
    { value: 'date_oldest', label: 'Oldest First' },
    { value: 'score_high', label: 'Score (High to Low)' },
    { value: 'score_low', label: 'Score (Low to High)' },
    { value: 'completeness_high', label: 'Completeness (High to Low)' },
    { value: 'completeness_low', label: 'Completeness (Low to High)' }
  ];

  // Clear all filters
  const clearAllFilters = () => {
    setFilterStatus('all');
    setFilterCertification('all');
    setFilterOrgType('all');
    setFilterSDG('all');
    setFilterCity('all');
    setSortBy('name_asc');
    setSearchQuery('');
  };

  // Count active filters
  const activeFilterCount = [
    filterStatus !== 'all',
    filterCertification !== 'all',
    filterOrgType !== 'all',
    filterSDG !== 'all',
    filterCity !== 'all'
  ].filter(Boolean).length;

  // Filter and sort actors
  const filteredActors = actors
    .filter(actor => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          actor.organization_name?.toLowerCase().includes(query) ||
          actor.city?.toLowerCase().includes(query) ||
          actor.social_mission?.toLowerCase().includes(query) ||
          actor.organization_type?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Organization type filter
      if (filterOrgType !== 'all' && actor.organization_type !== filterOrgType) {
        return false;
      }

      // SDG filter
      if (filterSDG !== 'all') {
        const sdgNum = parseInt(filterSDG);
        if (!actor.sdg_alignment?.includes(sdgNum)) {
          return false;
        }
      }

      // City filter
      if (filterCity !== 'all' && actor.city !== filterCity) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.organization_name || '').localeCompare(b.organization_name || '');
        case 'name_desc':
          return (b.organization_name || '').localeCompare(a.organization_name || '');
        case 'date_newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'date_oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'score_high':
          return (b.assessment_scores?.overall || 0) - (a.assessment_scores?.overall || 0);
        case 'score_low':
          return (a.assessment_scores?.overall || 0) - (b.assessment_scores?.overall || 0);
        case 'completeness_high':
          return calculateCompleteness(b).percentage - calculateCompleteness(a).percentage;
        case 'completeness_low':
          return calculateCompleteness(a).percentage - calculateCompleteness(b).percentage;
        default:
          return 0;
      }
    });

  const handleEditActor = (actor) => {
    setEditingActor(actor);
    setFormData({
      organization_name: actor.organization_name,
      organization_type: actor.organization_type,
      legal_status: actor.legal_status,
      legal_form: actor.legal_form || '',
      tax_id: actor.tax_id || '',
      registration_number: actor.registration_number || '',
      year_established: actor.year_established || '',
      email: actor.email,
      phone: actor.phone || '',
      website: actor.website || '',
      address: actor.address || '',
      city: actor.city || '',
      country: actor.country || 'Greece',
      social_mission: actor.social_mission,
      primary_goals: actor.primary_goals?.join(', ') || '',
      target_beneficiaries: actor.target_beneficiaries || '',
      sdg_alignment: actor.sdg_alignment || [],
      annual_revenue: actor.financial_indicators?.annual_revenue ?? '',
      funding_sources: actor.financial_indicators?.funding_sources || [],
      total_employees: actor.operational_indicators?.total_employees ?? '',
      volunteers: actor.operational_indicators?.volunteers ?? '',
      members: actor.operational_indicators?.members ?? '',
      beneficiaries_served: actor.operational_indicators?.beneficiaries_served ?? '',
      is_public: actor.is_public !== false,
      hide_financial_data: !!actor.hide_financial_data,
      hide_contact_info: !!actor.hide_contact_info
    });
    // Load existing documents (backend stores them under `uploaded_documents`)
    setExistingDocuments(actor.uploaded_documents || actor.documents || []);
    setEditUploadedFiles([]);
    setShowEditDialog(true);
  };

  // Handle file selection for edit dialog
  const handleEditFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setEditUploadedFiles(prev => [...prev, {
          filename: file.name,
          content_type: file.type,
          file_data: event.target.result.split(',')[1], // Remove data URL prefix
          document_name: file.name.replace(/\.[^/.]+$/, ''), // Default name without extension
          document_type: 'supporting'
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload documents for existing actor
  const handleUploadEditDocuments = async () => {
    if (!editingActor || editUploadedFiles.length === 0) return;
    
    const missingNames = editUploadedFiles.filter(f => !f.document_name.trim());
    if (missingNames.length > 0) {
      toast.error('Please provide a name for all documents');
      return;
    }
    
    setUploadingDocument(true);
    try {
      for (const file of editUploadedFiles) {
        await axios.post(`${API}/api/social-actors/${editingActor.id}/documents`, file, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
      toast.success(`${editUploadedFiles.length} document(s) uploaded successfully!`);
      setEditUploadedFiles([]);
      // Refresh the actor data to show new documents
      const response = await axios.get(`${API}/api/social-actors/${editingActor.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setExistingDocuments(response.data.uploaded_documents || response.data.documents || []);
      fetchActors();
    } catch (error) {
      console.error('Failed to upload documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploadingDocument(false);
    }
  };

  // Delete a document from an actor
  const handleDeleteDocument = async (documentId) => {
    if (!editingActor) return;
    
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/api/social-actors/${editingActor.id}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Document deleted successfully');
      setExistingDocuments(prev => prev.filter(d => d.id !== documentId));
      fetchActors();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  // Comparison Tool Functions
  const toggleCompareSelection = (actor) => {
    if (selectedForComparison.find(a => a.id === actor.id)) {
      setSelectedForComparison(prev => prev.filter(a => a.id !== actor.id));
    } else if (selectedForComparison.length < 3) {
      setSelectedForComparison(prev => [...prev, actor]);
    } else {
      toast.error('You can compare up to 3 organizations');
    }
  };

  const isSelectedForComparison = (actorId) => {
    return selectedForComparison.some(a => a.id === actorId);
  };

  const openCompareDialog = () => {
    if (selectedForComparison.length < 2) {
      toast.error('Select at least 2 organizations to compare');
      return;
    }
    setShowCompareDialog(true);
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setSelectedForComparison([]);
  };

  // Activity History Functions
  const fetchActivityHistory = async (actorId) => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API}/api/social-actors/${actorId}/activity`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setActivityHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch activity history:', error);
      // If endpoint doesn't exist yet, show mock data structure
      setActivityHistory([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const openActivityDialog = (actor) => {
    setSelectedActor(actor);
    fetchActivityHistory(actor.id);
    setShowActivityDialog(true);
  };

  const handleUpdateActor = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        organization_name: formData.organization_name,
        organization_type: formData.organization_type,
        legal_status: formData.legal_status,
        legal_form: formData.legal_form,
        tax_id: formData.tax_id,
        registration_number: formData.registration_number,
        year_established: formData.year_established ? parseInt(formData.year_established) : null,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        social_mission: formData.social_mission,
        primary_goals: formData.primary_goals.split(',').map(g => g.trim()).filter(g => g),
        target_beneficiaries: formData.target_beneficiaries,
        sdg_alignment: formData.sdg_alignment.map(s => parseInt(s)),
        // Financial Information → financial_indicators bucket (merges with existing)
        financial_indicators: {
          ...(editingActor?.financial_indicators || {}),
          annual_revenue: formData.annual_revenue !== '' && formData.annual_revenue != null ? parseFloat(formData.annual_revenue) : null,
          funding_sources: formData.funding_sources || [],
        },
        // Workforce Details → operational_indicators bucket (merges with existing)
        operational_indicators: {
          ...(editingActor?.operational_indicators || {}),
          total_employees: formData.total_employees !== '' && formData.total_employees != null ? parseInt(formData.total_employees) : null,
          volunteers: formData.volunteers !== '' && formData.volunteers != null ? parseInt(formData.volunteers) : null,
          members: formData.members !== '' && formData.members != null ? parseInt(formData.members) : null,
          beneficiaries_served: formData.beneficiaries_served !== '' && formData.beneficiaries_served != null ? parseInt(formData.beneficiaries_served) : null,
        },
        // Privacy Settings
        is_public: !!formData.is_public,
        hide_financial_data: !!formData.hide_financial_data,
        hide_contact_info: !!formData.hide_contact_info
      };
      
      const response = await axios.patch(`${API}/api/social-actors/${editingActor.id}`, updateData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Check if response indicates approval needed or direct update
      if (response.data.message && response.data.message.includes('approval')) {
        toast.success('Profile changes submitted for approval.');
      } else {
        toast.success('Profile updated successfully!');
      }
      
      setShowEditDialog(false);
      setEditingActor(null);
      resetForm();
      fetchActors();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleDeleteActor = async (actorId) => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/social-actors/${actorId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Organization deleted successfully!');
      fetchActors();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const viewActorDetails = async (actorId) => {
    try {
      const response = await axios.get(`${API}/api/social-actors/${actorId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSelectedActor(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to fetch actor details:', error);
      toast.error('Failed to load profile details');
    }
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      organization_type: 'social_enterprise',
      legal_status: '',
      registration_number: '',
      year_established: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      city: '',
      country: 'Greece',
      social_mission: '',
      primary_goals: '',
      target_beneficiaries: '',
      sdg_alignment: [],
      annual_revenue: '',
      funding_sources: [],
      total_employees: '',
      volunteers: '',
      members: '',
      beneficiaries_served: '',
      is_public: true,
      hide_financial_data: false,
      hide_contact_info: false
    });
    setUploadedFiles([]);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'draft': <Badge variant="secondary">Draft</Badge>,
      'pending_approval': <Badge className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>,
      'approved': <Badge className="bg-green-100 text-green-800">Approved</Badge>,
      'rejected': <Badge variant="destructive">Rejected</Badge>,
      'certified': <Badge className="bg-blue-100 text-blue-800">Certified</Badge>
    };
    
    return badges[status] || <Badge>Unknown</Badge>;
  };

  const canCreateProfile = user?.role === 'social_actor' || user?.role === 'administrator' || user?.role === 'project_manager';
  const canReview = user?.role === 'administrator' || user?.role === 'project_manager';
  const canEdit = (actor) => {
    if (!user || !actor) return false;
    if (actor.is_mine) return true;
    if (user.role === 'administrator' || user.role === 'project_manager') return true;
    if (user?.role === 'social_actor' && actor?.user_id === user?.id) return true;
    // Administrators can edit any actor
    if (user?.role === 'administrator') return true;
    // Project managers can edit any actor
    if (user?.role === 'project_manager') return true;
    // Social actors can edit their own profiles
    if (user?.role === 'social_actor' && actor?.user_id === user?.id) return true;
    return false;
  };

  // Export to PDF function
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCertification !== 'all') params.append('certification_level', filterCertification);
      if (filterOrgType !== 'all') params.append('organization_type', filterOrgType);
      
      const url = `${API}/api/social-actors/export/pdf${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `social_actors_registry_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Export single actor to PDF
  const handleExportActorPDF = async (actorId, orgName) => {
    try {
      const response = await axios.get(`${API}/api/social-actors/${actorId}/export/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${orgName.replace(/\s+/g, '_').toLowerCase()}_profile.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Profile PDF exported successfully!');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export profile PDF');
    }
  };

  // Download the Market Testing Report (.docx) for an actor — gathers the
  // organisation's cross-module engagement (Social Actor Registry, SI
  // Registry, Participatory Design, BI Plans, Evaluations, Projects,
  // Capacity Building, Continuous Improvement) and produces audit-grade
  // evidence that the actor is an external pilot user of the platform.
  const handleExportMarketTestingReport = async (actorId, orgName, lang = 'en') => {
    try {
      toast.info(lang === 'el' ? 'Παραγωγή αναφοράς Market Testing...' : 'Generating Market Testing report...');
      const response = await axios.get(
        `${API}/api/market-testing/actor/${actorId}/report?lang=${lang}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const slug = orgName.replace(/\s+/g, '_');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `Market_Testing_Report_${slug}_${today}${lang === 'el' ? '_EL' : ''}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(lang === 'el' ? 'Έτοιμη η αναφορά!' : 'Market Testing report ready!');
    } catch (error) {
      console.error('Failed to generate Market Testing report:', error);
      toast.error(error?.response?.data?.detail || 'Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading social actors...</div>
      </div>
    );
  }

  // Reusable form sections (used in BOTH Register New Organization AND Edit dialogs)
  // to mirror Step 2 (Financial Info), Step 3 (Workforce Details) and the
  // Privacy Settings described in the Social Actor Registry user guide.
  const renderExtendedFormSections = (idPrefix = '') => {
    const FUNDING_SOURCE_OPTIONS = ['grants', 'earned_income', 'donations', 'memberships', 'public_funding', 'sponsorships', 'crowdfunding', 'other'];
    const toggleFundingSource = (src) => {
      setFormData((prev) => ({
        ...prev,
        funding_sources: (prev.funding_sources || []).includes(src)
          ? prev.funding_sources.filter((s) => s !== src)
          : [...(prev.funding_sources || []), src],
      }));
    };
    return (
      <>
        {/* Step 2: Financial Information */}
        <div className="col-span-2 border-t pt-4">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-700" />
            {t('social_actor_registry.section_financial_title', 'Financial Information')}
          </h3>
          <p className="text-xs text-gray-500 mb-3">{t('social_actor_registry.section_financial_subtitle', 'Latest reported annual revenue and primary funding sources. Financial statements can be added in the Documents section below.')}</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              <Label htmlFor={`${idPrefix}annual_revenue`}>{t('social_actor_registry.field_annual_revenue', 'Annual Revenue (€)')}</Label>
              <Input
                id={`${idPrefix}annual_revenue`}
                type="number"
                min="0"
                step="0.01"
                value={formData.annual_revenue}
                onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
                placeholder="e.g. 245000"
              />
              <p className="text-xs text-gray-500 mt-1">{t('social_actor_registry.field_annual_revenue_helper', 'Latest reported annual revenue in euros.')}</p>
            </div>

            <div className="col-span-2 md:col-span-1">
              <Label>{t('social_actor_registry.field_funding_sources', 'Funding Sources')}</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {FUNDING_SOURCE_OPTIONS.map((src) => {
                  const sel = (formData.funding_sources || []).includes(src);
                  return (
                    <button
                      type="button"
                      key={src}
                      data-testid={`${idPrefix}funding-source-${src}`}
                      onClick={() => toggleFundingSource(src)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${sel ? 'bg-emerald-700 border-emerald-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      aria-pressed={sel}
                    >
                      {t(`social_actor_registry.funding_source_${src}`, src.replace(/_/g, ' '))}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('social_actor_registry.field_funding_sources_helper', 'Select all that apply.')}</p>
            </div>
          </div>
        </div>

        {/* Step 3: Workforce Details */}
        <div className="col-span-2 border-t pt-4">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" />
            {t('social_actor_registry.section_workforce_title', 'Workforce Details')}
          </h3>
          <p className="text-xs text-gray-500 mb-3">{t('social_actor_registry.section_workforce_subtitle', 'Full-time equivalent counts and annual beneficiary reach.')}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor={`${idPrefix}total_employees`}>{t('social_actor_registry.field_total_employees', 'Total Employees (FTE)')}</Label>
              <Input id={`${idPrefix}total_employees`} type="number" min="0" value={formData.total_employees}
                onChange={(e) => setFormData({ ...formData, total_employees: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}volunteers`}>{t('social_actor_registry.field_volunteers', 'Active Volunteers')}</Label>
              <Input id={`${idPrefix}volunteers`} type="number" min="0" value={formData.volunteers}
                onChange={(e) => setFormData({ ...formData, volunteers: e.target.value })} />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}members`}>{t('social_actor_registry.field_members', 'Members')}</Label>
              <Input id={`${idPrefix}members`} type="number" min="0" value={formData.members}
                onChange={(e) => setFormData({ ...formData, members: e.target.value })}
                placeholder={t('social_actor_registry.field_members_placeholder', 'For cooperatives')} />
            </div>
            <div>
              <Label htmlFor={`${idPrefix}beneficiaries_served`}>{t('social_actor_registry.field_beneficiaries_served', 'Beneficiaries Served (annual)')}</Label>
              <Input id={`${idPrefix}beneficiaries_served`} type="number" min="0" value={formData.beneficiaries_served}
                onChange={(e) => setFormData({ ...formData, beneficiaries_served: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="col-span-2 border-t pt-4">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <Eye className="h-4 w-4 text-purple-600" />
            {t('social_actor_registry.section_privacy_title', 'Privacy Settings')}
          </h3>
          <p className="text-xs text-gray-500 mb-3">{t('social_actor_registry.section_privacy_subtitle', 'Choose what is publicly visible on your organisation profile.')}</p>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                data-testid={`${idPrefix}privacy-is-public`}
              />
              <span>
                <span className="text-sm font-medium">{t('social_actor_registry.privacy_is_public', 'Make profile publicly visible')}</span>
                <span className="block text-xs text-gray-500">{t('social_actor_registry.privacy_is_public_helper', 'Allows investors and the public registry to see your organisation. Internal roles always see it.')}</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!formData.hide_financial_data}
                onChange={(e) => setFormData({ ...formData, hide_financial_data: e.target.checked })}
                data-testid={`${idPrefix}privacy-hide-financial`}
              />
              <span>
                <span className="text-sm font-medium">{t('social_actor_registry.privacy_hide_financial', 'Hide financial data from non-owners')}</span>
                <span className="block text-xs text-gray-500">{t('social_actor_registry.privacy_hide_financial_helper', 'Annual revenue and funding sources are hidden from peers and the public; admins, PMs and you still see them.')}</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!formData.hide_contact_info}
                onChange={(e) => setFormData({ ...formData, hide_contact_info: e.target.checked })}
                data-testid={`${idPrefix}privacy-hide-contact`}
              />
              <span>
                <span className="text-sm font-medium">{t('social_actor_registry.privacy_hide_contact', 'Hide contact information from non-owners')}</span>
                <span className="block text-xs text-gray-500">{t('social_actor_registry.privacy_hide_contact_helper', 'Email and phone are hidden from peers and the public; admins, PMs and you still see them.')}</span>
              </span>
            </label>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('social_actor_registry.back_to_dashboard')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('social_actor_registry.title')}</h1>
            <p className="text-gray-600 mt-1">
              {t('social_actor_registry.subtitle')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {/* Compare Mode Toggle */}
          {compareMode ? (
            <>
              <Badge variant="secondary" className="px-3 py-1">
                {t('social_actor_registry.compare_selected', { count: selectedForComparison.length })}
              </Badge>
              <Button
                variant="default"
                onClick={openCompareDialog}
                disabled={selectedForComparison.length < 2}
                className="flex items-center gap-2"
              >
                <GitCompare className="h-4 w-4" />
                {t('social_actor_registry.compare_count', { count: selectedForComparison.length })}
              </Button>
              <Button
                variant="outline"
                onClick={exitCompareMode}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t('social_actor_registry.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={() => setCompareMode(true)}
              className="flex items-center gap-2"
            >
              <GitCompare className="h-4 w-4" />
              {t('social_actor_registry.compare_label')}
            </Button>
          )}
          
          <Button
            variant={showAnalytics ? "default" : "outline"}
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2"
          >
            <PieChart className="h-4 w-4" />
            {showAnalytics ? t('social_actor_registry.hide_analytics') : t('social_actor_registry.show_analytics')}
          </Button>
          
          <Button
            variant={showMapView ? "default" : "outline"}
            onClick={() => setShowMapView(!showMapView)}
            className="flex items-center gap-2"
          >
            <Map className="h-4 w-4" />
            {showMapView ? t('social_actor_registry.hide_map') : t('social_actor_registry.show_map')}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {exporting ? t('social_actor_registry.exporting') : t('social_actor_registry.export_pdf')}
          </Button>
          
          {(user?.role === 'administrator' || user?.role === 'project_manager') && (
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              className="flex items-center gap-2"
              data-testid="import-csv-btn"
            >
              <Upload className="h-4 w-4" />
              {t('social_actor_registry.import_csv')}
            </Button>
          )}
          
          {canCreateProfile && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('social_actor_registry.register_new_org')}
            </Button>
          )}
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-600" />
                {t('social_actor_registry.analytics_dashboard')}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAnalytics(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <SocialActorAnalytics actors={actors} />
          </CardContent>
        </Card>
      )}

      {/* Map View */}
      {showMapView && (
        <SocialActorMapView 
          actors={actors} 
          onViewDetails={(actor) => {
            setSelectedActor(actor);
            setShowDetailDialog(true);
          }}
          isAdmin={user?.role === 'administrator' || user?.role === 'project_manager'}
          onBatchGeocode={async () => {
            try {
              const response = await axios.post(`${API}/api/social-actors/batch-geocode`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              });
              const result = response.data;
              if (result.success > 0) {
                toast.success(t('social_actor_registry.geocode_success', { count: result.success }));
                fetchActors(); // Refresh the list
              }
              if (result.failed > 0) {
                toast.warning(t('social_actor_registry.geocode_partial_failure', { count: result.failed }));
              }
            } catch (error) {
              console.error('Batch geocoding failed:', error);
              toast.error(t('social_actor_registry.geocode_failed'));
            }
          }}
        />
      )}

      {/* Compare Mode Banner */}
      {compareMode && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitCompare className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">
                  {t('social_actor_registry.compare_banner')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {selectedForComparison.map(actor => (
                  <Badge key={actor.id} variant="secondary" className="flex items-center gap-1">
                    {actor.organization_name.substring(0, 20)}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-red-600" 
                      onClick={() => toggleCompareSelection(actor)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          {/* Primary Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="sar-search-input">{t('social_actor_registry.search_label')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
                <Input
                  id="sar-search-input"
                  placeholder={t('social_actor_registry.search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="sar-search-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="sar-filter-status-trigger">{t('social_actor_registry.filter_status_label')}</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger id="sar-filter-status-trigger" data-testid="sar-filter-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('social_actor_registry.all_status')}</SelectItem>
                  <SelectItem value="draft">{t('social_actor_registry.status_draft')}</SelectItem>
                  <SelectItem value="pending_approval">{t('social_actor_registry.status_pending_approval')}</SelectItem>
                  <SelectItem value="approved">{t('social_actor_registry.status_approved')}</SelectItem>
                  <SelectItem value="certified">{t('social_actor_registry.status_certified')}</SelectItem>
                  <SelectItem value="rejected">{t('social_actor_registry.status_rejected')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="sar-sort-by-trigger">{t('social_actor_registry.sort_by_label')}</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger id="sar-sort-by-trigger" data-testid="sar-sort-by-trigger">
                  <ArrowUpDown className="h-4 w-4 mr-2" aria-hidden="true" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end gap-2">
              <Button
                variant={showAdvancedFilters ? "default" : "outline"}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex-1"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                {t('social_actor_registry.filters')}
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                    {activeFilterCount}
                  </Badge>
                )}
                {showAdvancedFilters ? (
                  <ChevronUp className="h-4 w-4 ml-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-1" />
                )}
              </Button>
            </div>
          </div>

          {/* Advanced Filters (Collapsible) */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">{t('social_actor_registry.advanced_filters')}</span>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-3 w-3 mr-1" />
                    {t('social_actor_registry.clear_all')}
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>{t('social_actor_registry.org_type_label')}</Label>
                  <Select value={filterOrgType} onValueChange={setFilterOrgType}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('social_actor_registry.all_types')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('social_actor_registry.all_types')}</SelectItem>
                      {orgTypeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('social_actor_registry.sdg_label')}</Label>
                  <Select value={filterSDG} onValueChange={setFilterSDG}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('social_actor_registry.all_sdgs')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('social_actor_registry.all_sdgs')}</SelectItem>
                      {sdgOptions.map(sdg => (
                        <SelectItem key={sdg} value={sdg.toString()}>
                          SDG {sdg}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('social_actor_registry.certification_label')}</Label>
                  <Select value={filterCertification} onValueChange={setFilterCertification}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('social_actor_registry.all_levels')}</SelectItem>
                      <SelectItem value="level_3_excellence">{t('social_actor_registry.level_a')}</SelectItem>
                      <SelectItem value="level_2_commitment">{t('social_actor_registry.level_b')}</SelectItem>
                      <SelectItem value="level_1_basic">{t('social_actor_registry.level_c')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('social_actor_registry.city_label')}</Label>
                  <Select value={filterCity} onValueChange={setFilterCity}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('social_actor_registry.all_cities')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('social_actor_registry.all_cities')}</SelectItem>
                      {uniqueCities.map(city => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredActors.length}</span> of{' '}
              <span className="font-semibold">{actors.length}</span> organizations
            </div>
            <Button variant="outline" size="sm" onClick={fetchActors}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Social Actors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredActors.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">
            {searchQuery ? `No organizations found matching "${searchQuery}"` : 'No social actors found'}
          </div>
        ) : (
          filteredActors.map((actor) => {
            const isOwnOrganization = actor.is_mine || actor.user_id === user?.id;
            const completeness = calculateCompleteness(actor);
            const isSelected = isSelectedForComparison(actor.id);
            return (
            <Card 
              key={actor.id} 
              className={`hover:shadow-lg transition-shadow ${
                isOwnOrganization ? 'border-2 border-blue-500 bg-blue-50/30' : ''
              } ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {/* Compare Mode Checkbox */}
                      {compareMode && (
                        <button
                          onClick={() => toggleCompareSelection(actor)}
                          className="mr-1"
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-500 hover:text-blue-500" />
                          )}
                        </button>
                      )}
                      <CardTitle className="text-lg">{actor.organization_name}</CardTitle>
                      {isOwnOrganization && (
                        <Badge className="bg-blue-600 text-white text-xs">
                          Your Organization
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 capitalize">
                      {actor.organization_type?.replace('_', ' ') || 'Unknown'}
                    </p>
                  </div>
                  {getCertificationBadge(actor.certification_level)}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  {getStatusBadge(actor.status)}
                </div>
                
                {/* Tags Display */}
                {actor.tags && actor.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {actor.tags.map((tag, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-700 text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {actor.city && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    {actor.city}, {actor.country}
                  </div>
                )}
                
                <p className="text-sm text-gray-700 line-clamp-2">
                  {actor.social_mission}
                </p>
                
                {actor.sdg_alignment && actor.sdg_alignment.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {actor.sdg_alignment.slice(0, 3).map(sdg => (
                      <Badge key={sdg} variant="outline" className="text-xs">
                        SDG {sdg}
                      </Badge>
                    ))}
                    {actor.sdg_alignment.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{actor.sdg_alignment.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Profile Completeness Indicator */}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Profile Completeness
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {completeness.percentage}%
                    </span>
                  </div>
                  <Progress value={completeness.percentage} className="h-2" />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => viewActorDetails(actor.id)}
                    className="flex-1"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  
                  {/* Activity History Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openActivityDialog(actor)}
                    className="text-gray-600"
                    title="View Activity History"
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  
                  {/* QR Code Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setQRCodeActor(actor);
                      setShowQRCode(true);
                    }}
                    className="text-purple-600"
                    title="Generate QR Code"
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                  
                  {actor.status === 'draft' && (actor.is_mine || actor.user_id === user?.id) && (
                    <Button
                      size="sm"
                      onClick={() => handleSubmitForApproval(actor.id)}
                      className="flex-1"
                    >
                      Submit
                    </Button>
                  )}
                  
                  {/* PM and Admin can edit/delete any organization */}
                  {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditActor(actor)}
                        className="text-blue-600 hover:bg-blue-50 border-blue-300"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteActor(actor.id)}
                        className="text-red-600 hover:bg-red-50 border-red-300"
                        aria-label={`Delete ${actor.name || 'organization'}`}
                        title="Delete organization"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                  
                  {/* Social actor can edit/delete their own organization */}
                  {((actor.is_mine && user?.role === 'social_actor') || (actor.user_id === user?.id && user?.role === 'social_actor')) && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditActor(actor)}
                        className="text-blue-600 hover:bg-blue-50 border-blue-300"
                        data-testid={`edit-own-actor-${actor.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" aria-hidden="true" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteActor(actor.id)}
                        className="text-red-600 hover:bg-red-50 border-red-300"
                        aria-label={`Delete ${actor.name || 'organization'}`}
                        title="Delete organization"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                  
                  {actor.status === 'pending_approval' && canReview && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleReviewActor(actor.id, 'approve', 'level_1_commitment')}
                        className="bg-green-700 hover:bg-green-800"
                      >
                        <CheckCircle className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReviewActor(actor.id, 'reject')}
                      >
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
          })
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('social_actor_registry.create_title')}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateActor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="organization_name">{t('social_actor_registry.field_organization_name')} *</Label>
                <Input
                  id="organization_name"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="organization_type">{t('social_actor_registry.field_organization_type')} *</Label>
                <Select
                  value={formData.organization_type}
                  onValueChange={(value) => setFormData({...formData, organization_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_enterprise">{t('social_actor_registry.type_social_enterprise')}</SelectItem>
                    <SelectItem value="non_profit">{t('social_actor_registry.type_non_profit')}</SelectItem>
                    <SelectItem value="cooperative">{t('social_actor_registry.type_cooperative')}</SelectItem>
                    <SelectItem value="ngo">{t('social_actor_registry.type_ngo')}</SelectItem>
                    <SelectItem value="other">{t('social_actor_registry.type_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="legal_status">{t('social_actor_registry.field_legal_status')} *</Label>
                <Input
                  id="legal_status"
                  value={formData.legal_status}
                  onChange={(e) => setFormData({...formData, legal_status: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="legal_form">{t('social_actor_registry.field_legal_form')}</Label>
                <Select
                  value={formData.legal_form}
                  onValueChange={(value) => setFormData({...formData, legal_form: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('social_actor_registry.select_legal_form')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ΚΟΙΝΣΕΠ">ΚΟΙΝΣΕΠ (Social Cooperative Enterprise)</SelectItem>
                    <SelectItem value="ΑΜΚΕ">ΑΜΚΕ (Civil Non-Profit Company)</SelectItem>
                    <SelectItem value="ΑΕ">ΑΕ (Societe Anonyme)</SelectItem>
                    <SelectItem value="ΕΠΕ">ΕΠΕ (Limited Liability Company)</SelectItem>
                    <SelectItem value="ΙΚΕ">ΙΚΕ (Private Company)</SelectItem>
                    <SelectItem value="Συνεταιρισμός">Συνεταιρισμός (Cooperative)</SelectItem>
                    <SelectItem value="Σωματείο">Σωματείο (Association)</SelectItem>
                    <SelectItem value="Ίδρυμα">Ίδρυμα (Foundation)</SelectItem>
                    <SelectItem value="Other">{t('social_actor_registry.type_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="tax_id">{t('social_actor_registry.field_tax_id')}</Label>
                <Input
                  id="tax_id"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                  placeholder={t('social_actor_registry.tax_id_placeholder')}
                />
              </div>
              
              <div>
                <Label htmlFor="registration_number">{t('social_actor_registry.field_registration_number')}</Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="year_established">{t('social_actor_registry.field_year_established')}</Label>
                <Input
                  id="year_established"
                  type="number"
                  value={formData.year_established}
                  onChange={(e) => setFormData({...formData, year_established: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="email">{t('social_actor_registry.field_email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="phone">{t('social_actor_registry.field_phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="website">{t('social_actor_registry.field_website')}</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="city">{t('social_actor_registry.field_city')}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="address">{t('social_actor_registry.field_address')}</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="social_mission">{t('social_actor_registry.field_social_mission')} *</Label>
                <Textarea
                  id="social_mission"
                  value={formData.social_mission}
                  onChange={(e) => setFormData({...formData, social_mission: e.target.value})}
                  rows={3}
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="target_beneficiaries">{t('social_actor_registry.field_target_beneficiaries')}</Label>
                <Textarea
                  id="target_beneficiaries"
                  value={formData.target_beneficiaries}
                  onChange={(e) => setFormData({...formData, target_beneficiaries: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="primary_goals">{t('social_actor_registry.field_primary_goals', 'Primary goals')}</Label>
                <Input
                  id="primary_goals"
                  value={formData.primary_goals}
                  onChange={(e) => setFormData({...formData, primary_goals: e.target.value})}
                  placeholder={t('social_actor_registry.field_primary_goals_placeholder', 'Comma-separated, e.g. Social cohesion, Skills development, Community participation')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('social_actor_registry.field_primary_goals_helper', 'Comma-separated. Each item is saved as a separate goal.')}</p>
              </div>

              <div className="col-span-2">
                <Label>{t('social_actor_registry.field_sdg_alignment', 'SDG Alignment')}</Label>
                <p className="text-xs text-gray-500 mb-2">{t('social_actor_registry.field_sdg_alignment_helper', 'Select all 17 UN Sustainable Development Goals your organisation contributes to.')}</p>
                <div className="grid grid-cols-6 sm:grid-cols-9 gap-2">
                  {Array.from({length: 17}, (_, i) => i + 1).map((sdg) => {
                    const selected = formData.sdg_alignment.includes(sdg);
                    return (
                      <button
                        key={sdg}
                        type="button"
                        data-testid={`sdg-toggle-${sdg}`}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            sdg_alignment: prev.sdg_alignment.includes(sdg)
                              ? prev.sdg_alignment.filter((x) => x !== sdg)
                              : [...prev.sdg_alignment, sdg].sort((a, b) => a - b),
                          }));
                        }}
                        className={`px-2 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                          selected
                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={selected}
                      >
                        SDG {sdg}
                      </button>
                    );
                  })}
                </div>
                {formData.sdg_alignment.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">{t('social_actor_registry.field_sdg_alignment_selected', '{{count}} selected', { count: formData.sdg_alignment.length })}</p>
                )}
              </div>

              {renderExtendedFormSections('reg_')}

              <div className="col-span-2 border-t pt-4">
                <Label htmlFor="file_upload">{t('social_actor_registry.upload_documents_label')}</Label>
                <p className="text-sm text-gray-500 mb-2">
                  {t('social_actor_registry.upload_documents_helper')}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="file_upload"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                    className="flex-1"
                  />
                  <Upload className="h-5 w-5 text-gray-500" />
                </div>
                
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-3">
                    <Label className="text-sm font-medium">{t('social_actor_registry.uploaded_files', { count: uploadedFiles.length })}</Label>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium">{file.filename}</span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2 pl-6">
                          <div>
                            <Label htmlFor={`doc-name-${index}`} className="text-xs text-gray-600">
                              {t('social_actor_registry.doc_name_label')} *
                            </Label>
                            <Input
                              id={`doc-name-${index}`}
                              value={file.document_name}
                              onChange={(e) => updateFileMetadata(index, 'document_name', e.target.value)}
                              placeholder={t('social_actor_registry.doc_name_placeholder')}
                              className="mt-1 text-sm"
                              required
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor={`doc-desc-${index}`} className="text-xs text-gray-600">
                              {t('social_actor_registry.doc_desc_label')}
                            </Label>
                            <Textarea
                              id={`doc-desc-${index}`}
                              value={file.document_description}
                              onChange={(e) => updateFileMetadata(index, 'document_description', e.target.value)}
                              placeholder={t('social_actor_registry.doc_desc_placeholder')}
                              rows={2}
                              className="mt-1 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                {t('social_actor_registry.cancel')}
              </Button>
              <Button type="submit">
                {t('social_actor_registry.create_submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('social_actor_registry.edit_title')}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateActor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="edit-organization_name">{t('social_actor_registry.field_organization_name')} *</Label>
                <Input
                  id="edit-organization_name"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-organization_type">{t('social_actor_registry.field_organization_type')} *</Label>
                <Select
                  value={formData.organization_type}
                  onValueChange={(value) => setFormData({...formData, organization_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social_enterprise">{t('social_actor_registry.type_social_enterprise')}</SelectItem>
                    <SelectItem value="non_profit">{t('social_actor_registry.type_non_profit')}</SelectItem>
                    <SelectItem value="cooperative">{t('social_actor_registry.type_cooperative')}</SelectItem>
                    <SelectItem value="ngo">{t('social_actor_registry.type_ngo')}</SelectItem>
                    <SelectItem value="other">{t('social_actor_registry.type_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-legal_status">{t('social_actor_registry.field_legal_status')} *</Label>
                <Input
                  id="edit-legal_status"
                  value={formData.legal_status}
                  onChange={(e) => setFormData({...formData, legal_status: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-legal_form">{t('social_actor_registry.field_legal_form')}</Label>
                <Select
                  value={formData.legal_form}
                  onValueChange={(value) => setFormData({...formData, legal_form: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('social_actor_registry.select_legal_form')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ΚΟΙΝΣΕΠ">ΚΟΙΝΣΕΠ (Social Cooperative Enterprise)</SelectItem>
                    <SelectItem value="ΑΜΚΕ">ΑΜΚΕ (Civil Non-Profit Company)</SelectItem>
                    <SelectItem value="ΑΕ">ΑΕ (Societe Anonyme)</SelectItem>
                    <SelectItem value="ΕΠΕ">ΕΠΕ (Limited Liability Company)</SelectItem>
                    <SelectItem value="ΙΚΕ">ΙΚΕ (Private Company)</SelectItem>
                    <SelectItem value="Συνεταιρισμός">Συνεταιρισμός (Cooperative)</SelectItem>
                    <SelectItem value="Σωματείο">Σωματείο (Association)</SelectItem>
                    <SelectItem value="Ίδρυμα">Ίδρυμα (Foundation)</SelectItem>
                    <SelectItem value="Other">{t('social_actor_registry.type_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-tax_id">{t('social_actor_registry.field_tax_id')}</Label>
                <Input
                  id="edit-tax_id"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                  placeholder={t('social_actor_registry.tax_id_placeholder')}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-registration_number">{t('social_actor_registry.field_registration_number')}</Label>
                <Input
                  id="edit-registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-year_established">{t('social_actor_registry.field_year_established')}</Label>
                <Input
                  id="edit-year_established"
                  type="number"
                  value={formData.year_established}
                  onChange={(e) => setFormData({...formData, year_established: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-email">{t('social_actor_registry.field_email')} *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-phone">{t('social_actor_registry.field_phone')}</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-website">{t('social_actor_registry.field_website')}</Label>
                <Input
                  id="edit-website"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-city">{t('social_actor_registry.field_city')}</Label>
                <Input
                  id="edit-city"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="edit-address">{t('social_actor_registry.field_address')}</Label>
                <Input
                  id="edit-address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="edit-social_mission">{t('social_actor_registry.field_social_mission')} *</Label>
                <Textarea
                  id="edit-social_mission"
                  value={formData.social_mission}
                  onChange={(e) => setFormData({...formData, social_mission: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="edit-target_beneficiaries">{t('social_actor_registry.field_target_beneficiaries')}</Label>
                <Textarea
                  id="edit-target_beneficiaries"
                  value={formData.target_beneficiaries}
                  onChange={(e) => setFormData({...formData, target_beneficiaries: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="edit-primary_goals">{t('social_actor_registry.field_primary_goals', 'Primary goals')}</Label>
                <Input
                  id="edit-primary_goals"
                  value={formData.primary_goals}
                  onChange={(e) => setFormData({...formData, primary_goals: e.target.value})}
                  placeholder={t('social_actor_registry.field_primary_goals_placeholder', 'Comma-separated, e.g. Social cohesion, Skills development, Community participation')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('social_actor_registry.field_primary_goals_helper', 'Comma-separated. Each item is saved as a separate goal.')}</p>
              </div>

              <div className="col-span-2">
                <Label>{t('social_actor_registry.field_sdg_alignment', 'SDG Alignment')}</Label>
                <p className="text-xs text-gray-500 mb-2">{t('social_actor_registry.field_sdg_alignment_helper', 'Select all 17 UN Sustainable Development Goals your organisation contributes to.')}</p>
                <div className="grid grid-cols-6 sm:grid-cols-9 gap-2">
                  {Array.from({length: 17}, (_, i) => i + 1).map((sdg) => {
                    const selected = formData.sdg_alignment.includes(sdg);
                    return (
                      <button
                        key={sdg}
                        type="button"
                        data-testid={`edit-sdg-toggle-${sdg}`}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            sdg_alignment: prev.sdg_alignment.includes(sdg)
                              ? prev.sdg_alignment.filter((x) => x !== sdg)
                              : [...prev.sdg_alignment, sdg].sort((a, b) => a - b),
                          }));
                        }}
                        className={`px-2 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                          selected
                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={selected}
                      >
                        SDG {sdg}
                      </button>
                    );
                  })}
                </div>
                {formData.sdg_alignment.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">{t('social_actor_registry.field_sdg_alignment_selected', '{{count}} selected', { count: formData.sdg_alignment.length })}</p>
                )}
              </div>

              {renderExtendedFormSections('edit_')}
            </div>
            
            {/* Tags Management Section - Only for PM/Admin */}
            {(user?.role === 'administrator' || user?.role === 'project_manager') && editingActor && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {t('social_actor_registry.tags_section')}
                  </Label>
                </div>
                
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(editingActor.tags || []).map((tag, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-700 flex items-center gap-1 px-3 py-1">
                        {tag}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-red-600 ml-1" 
                          onClick={async () => {
                            const newTags = editingActor.tags.filter((_, i) => i !== idx);
                            try {
                              await axios.post(`${API}/api/social-actors/${editingActor.id}/tags`, 
                                { tags: newTags },
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                              );
                              setEditingActor({...editingActor, tags: newTags});
                              toast.success(t('social_actor_registry.tag_removed'));
                            } catch (error) {
                              toast.error(t('social_actor_registry.tag_remove_failed'));
                            }
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      id="new_tag"
                      placeholder={t('social_actor_registry.tag_input_placeholder')}
                      onKeyPress={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const newTag = e.target.value.trim();
                          if (newTag && !(editingActor.tags || []).includes(newTag)) {
                            const newTags = [...(editingActor.tags || []), newTag];
                            try {
                              await axios.post(`${API}/api/social-actors/${editingActor.id}/tags`,
                                { tags: newTags },
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                              );
                              setEditingActor({...editingActor, tags: newTags});
                              e.target.value = '';
                              toast.success(t('social_actor_registry.tag_added'));
                              fetchActors();
                            } catch (error) {
                              toast.error(t('social_actor_registry.tag_add_failed'));
                            }
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        const input = document.getElementById('new_tag');
                        const newTag = input.value.trim();
                        if (newTag && !(editingActor.tags || []).includes(newTag)) {
                          const newTags = [...(editingActor.tags || []), newTag];
                          try {
                            await axios.post(`${API}/api/social-actors/${editingActor.id}/tags`,
                              { tags: newTags },
                              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                            );
                            setEditingActor({...editingActor, tags: newTags});
                            input.value = '';
                            toast.success(t('social_actor_registry.tag_added'));
                            fetchActors();
                          } catch (error) {
                            toast.error(t('social_actor_registry.tag_add_failed'));
                          }
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Press Enter or click + to add a tag</p>
                </div>
              </div>
            )}
            
            {/* Document Management Section - Admin/PM always, or Social Actor on own profile */}
            {((user?.role === 'administrator' || user?.role === 'project_manager') ||
              (user?.role === 'social_actor' && editingActor && editingActor.user_id === user.id)) && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('social_actor_registry.documents_section')}
                  </Label>
                </div>
                
                {/* Existing Documents */}
                {existingDocuments.length > 0 && (
                  <div className="mb-4">
                    <Label className="text-sm text-gray-600 mb-2 block">{t('social_actor_registry.current_documents', { count: existingDocuments.length })}</Label>
                    <div className="space-y-2">
                      {existingDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-blue-500" />
                            <div>
                              <p className="font-medium text-sm">{doc.document_name || doc.filename}</p>
                              <p className="text-xs text-gray-500">{doc.document_type} • {doc.filename}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.file_url && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    // file_url is /api/...; resolve against backend and fetch with auth
                                    const url = doc.file_url.startsWith('http') ? doc.file_url : `${API}${doc.file_url}`;
                                    const resp = await axios.get(url, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                      responseType: 'blob',
                                    });
                                    const blob = new Blob([resp.data], { type: doc.content_type || 'application/octet-stream' });
                                    const objectUrl = window.URL.createObjectURL(blob);
                                    window.open(objectUrl, '_blank');
                                    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
                                  } catch (err) {
                                    console.error('Failed to open document:', err);
                                    toast.error('Failed to open document');
                                  }
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Upload New Documents */}
                <div className="space-y-3">
                  <Label htmlFor="edit_file_upload" className="text-sm text-gray-600">
                    {t('social_actor_registry.upload_new_documents')}
                  </Label>
                  <p className="text-xs text-gray-500">
                    {t('social_actor_registry.upload_documents_helper')}
                  </p>
                  
                  <Input
                    id="edit_file_upload"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={handleEditFileSelect}
                    className="cursor-pointer"
                  />
                  
                  {editUploadedFiles.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <Label className="text-sm font-medium">{t('social_actor_registry.files_to_upload', { count: editUploadedFiles.length })}</Label>
                      {editUploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <Upload className="h-4 w-4 text-blue-500" />
                          <div className="flex-1">
                            <Input
                              value={file.document_name}
                              onChange={(e) => {
                                const newFiles = [...editUploadedFiles];
                                newFiles[index].document_name = e.target.value;
                                setEditUploadedFiles(newFiles);
                              }}
                              placeholder={t('social_actor_registry.doc_name_input_placeholder')}
                              className="mb-1 h-8 text-sm"
                            />
                            <p className="text-xs text-gray-500">{file.filename}</p>
                          </div>
                          <Select
                            value={file.document_type}
                            onValueChange={(value) => {
                              const newFiles = [...editUploadedFiles];
                              newFiles[index].document_type = value;
                              setEditUploadedFiles(newFiles);
                            }}
                          >
                            <SelectTrigger className="w-32 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="registration">{t('social_actor_registry.doc_type_registration')}</SelectItem>
                              <SelectItem value="certificate">{t('social_actor_registry.doc_type_certificate')}</SelectItem>
                              <SelectItem value="financial">{t('social_actor_registry.doc_type_financial')}</SelectItem>
                              <SelectItem value="report">{t('social_actor_registry.doc_type_report')}</SelectItem>
                              <SelectItem value="supporting">{t('social_actor_registry.doc_type_supporting')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditUploadedFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ))}
                      
                      <Button
                        type="button"
                        onClick={handleUploadEditDocuments}
                        disabled={uploadingDocument}
                        className="mt-2"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingDocument ? t('social_actor_registry.uploading') : t('social_actor_registry.upload_n_documents', { count: editUploadedFiles.length })}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowEditDialog(false);
                setEditingActor(null);
                resetForm();
                setEditUploadedFiles([]);
                setExistingDocuments([]);
              }}>
                {t('social_actor_registry.cancel')}
              </Button>
              <Button type="submit">
                {t('social_actor_registry.update_submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {selectedActor && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{selectedActor.organization_name}</DialogTitle>
                <div className="flex items-center gap-2 mr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportActorPDF(selectedActor.id, selectedActor.organization_name)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportMarketTestingReport(selectedActor.id, selectedActor.organization_name, 'en')}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    title="Generate a Market Testing Report proving pilot usage across all modules"
                    data-testid="market-testing-report-en-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Market Testing · EN
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportMarketTestingReport(selectedActor.id, selectedActor.organization_name, 'el')}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    title="Λήψη αναφοράς Market Testing — αποδεικνύει την πιλοτική χρήση σε όλα τα modules"
                    data-testid="market-testing-report-el-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Market Testing · EL
                  </Button>
                </div>
              </div>
            </DialogHeader>
            
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="basic">
                  <Info className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('social_actor_registry.tab_basic')}</span>
                </TabsTrigger>
                <TabsTrigger value="scoring">
                  <Award className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('social_actor_registry.tab_cert')}</span>
                </TabsTrigger>
                <TabsTrigger value="nearby">
                  <Users className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('social_actor_registry.tab_nearby')}</span>
                </TabsTrigger>
                <TabsTrigger value="collaborations">
                  <Handshake className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('social_actor_registry.tab_collab')}</span>
                </TabsTrigger>
                <TabsTrigger value="assessment">
                  <BarChart3 className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{t('social_actor_registry.tab_assess')}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="flex gap-2">
                  {getStatusBadge(selectedActor.status)}
                  {getCertificationBadge(selectedActor.certification_level)}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">{t('social_actor_detail.basic_organization_type')}</Label>
                  <p className="capitalize">{selectedActor.organization_type?.replace('_', ' ') || t('social_actor_detail.basic_unknown')}</p>
                </div>
                
                <div>
                  <Label className="text-gray-600">{t('social_actor_detail.basic_legal_status')}</Label>
                  <p>{selectedActor.legal_status}</p>
                </div>
                
                {selectedActor.legal_form && (
                  <div>
                    <Label className="text-gray-600">{t('social_actor_detail.basic_legal_form')}</Label>
                    <p>{selectedActor.legal_form}</p>
                  </div>
                )}
                
                {selectedActor.tax_id && (
                  <div>
                    <Label className="text-gray-600">{t('social_actor_detail.basic_tax_id')}</Label>
                    <p>{selectedActor.tax_id}</p>
                  </div>
                )}
                
                {selectedActor.registration_number && (
                  <div>
                    <Label className="text-gray-600">{t('social_actor_detail.basic_registration_number')}</Label>
                    <p>{selectedActor.registration_number}</p>
                  </div>
                )}
                
                {selectedActor.year_established && (
                  <div>
                    <Label className="text-gray-600">{t('social_actor_detail.basic_year_established')}</Label>
                    <p>{selectedActor.year_established}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-gray-600">{t('social_actor_detail.basic_location')}</Label>
                  <p>{selectedActor.city}, {selectedActor.country}</p>
                </div>
                
                {selectedActor.address && (
                  <div className="col-span-2">
                    <Label className="text-gray-600">{t('social_actor_detail.basic_address')}</Label>
                    <p>{selectedActor.address}</p>
                  </div>
                )}
                
                {(() => {
                  const isOwner = selectedActor.user_id && user && selectedActor.user_id === user.id;
                  const isReviewer = user?.role === 'administrator' || user?.role === 'project_manager';
                  const hideContact = !!selectedActor.hide_contact_info && !isOwner && !isReviewer;
                  if (hideContact) {
                    return (
                      <div className="col-span-2">
                        <Label className="text-gray-600">{t('social_actor_detail.basic_contact', 'Contact')}</Label>
                        <p className="text-sm text-gray-500 italic">{t('social_actor_detail.basic_contact_hidden', 'Contact information hidden by the organisation. Please use the platform messaging to reach out.')}</p>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div>
                        <Label className="text-gray-600">{t('social_actor_detail.basic_email')}</Label>
                        <p>{selectedActor.email}</p>
                      </div>
                      {selectedActor.phone && (
                        <div>
                          <Label className="text-gray-600">{t('social_actor_detail.basic_phone')}</Label>
                          <p>{selectedActor.phone}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {selectedActor.website && (
                  <div className="col-span-2">
                    <Label className="text-gray-600">{t('social_actor_detail.basic_website')}</Label>
                    <a href={selectedActor.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {selectedActor.website}
                    </a>
                  </div>
                )}
                </div>
                
                <div>
                  <Label className="text-gray-600">{t('social_actor_detail.basic_social_mission')}</Label>
                  <p className="text-gray-800">{selectedActor.social_mission}</p>
                </div>
                
                {selectedActor.sdg_alignment && selectedActor.sdg_alignment.length > 0 && (
                  <div>
                    <Label className="text-gray-600">{t('social_actor_detail.basic_sdg_alignment')}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedActor.sdg_alignment.map(sdg => (
                        <Badge key={sdg} variant="outline">
                          SDG {sdg}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Financial Information — hidden from peers/public if hide_financial_data is set
                    and the viewer is neither the owner nor an admin/PM */}
                {(() => {
                  const fi = selectedActor.financial_indicators || {};
                  const hasFinancial = fi.annual_revenue != null || (fi.funding_sources && fi.funding_sources.length > 0);
                  if (!hasFinancial) return null;
                  const isOwner = selectedActor.user_id && user && selectedActor.user_id === user.id;
                  const isReviewer = user?.role === 'administrator' || user?.role === 'project_manager';
                  const canSee = isOwner || isReviewer || !selectedActor.hide_financial_data;
                  if (!canSee) return null;
                  const fmtRevenue = fi.annual_revenue != null
                    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(fi.annual_revenue)
                    : null;
                  return (
                    <div data-testid="view-financial-info" className="border-t pt-3">
                      <Label className="text-gray-600 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-emerald-700" />
                        {t('social_actor_detail.basic_financial_title', 'Financial Information')}
                      </Label>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        {fmtRevenue && (
                          <div>
                            <p className="text-xs text-gray-500">{t('social_actor_detail.basic_annual_revenue', 'Annual Revenue')}</p>
                            <p className="font-semibold text-emerald-700">{fmtRevenue}</p>
                          </div>
                        )}
                        {fi.funding_sources && fi.funding_sources.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500">{t('social_actor_detail.basic_funding_sources', 'Funding Sources')}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {fi.funding_sources.map((s) => (
                                <Badge key={s} variant="outline" className="capitalize">{String(s).replace(/_/g, ' ')}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Workforce Details */}
                {(() => {
                  const oi = selectedActor.operational_indicators || {};
                  const stats = [
                    { key: 'total_employees', label: t('social_actor_detail.basic_total_employees', 'Employees (FTE)'), value: oi.total_employees },
                    { key: 'volunteers', label: t('social_actor_detail.basic_volunteers', 'Volunteers'), value: oi.volunteers },
                    { key: 'members', label: t('social_actor_detail.basic_members', 'Members'), value: oi.members },
                    { key: 'beneficiaries_served', label: t('social_actor_detail.basic_beneficiaries_served', 'Beneficiaries served / year'), value: oi.beneficiaries_served },
                  ].filter((s) => s.value != null && s.value !== '');
                  if (stats.length === 0) return null;
                  return (
                    <div data-testid="view-workforce-info" className="border-t pt-3">
                      <Label className="text-gray-600 flex items-center gap-2">
                        <Users className="h-4 w-4 text-blue-600" />
                        {t('social_actor_detail.basic_workforce_title', 'Workforce Details')}
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                        {stats.map((s) => (
                          <div key={s.key} className="bg-gray-50 border rounded-md p-2 text-center">
                            <p className="text-xl font-bold text-gray-800">{new Intl.NumberFormat('de-DE').format(s.value)}</p>
                            <p className="text-xs text-gray-500">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Uploaded Documents */}
                <div data-testid="view-uploaded-documents">
                  <Label className="text-gray-600 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {t('social_actor_detail.basic_documents', 'Documents')}
                    {(selectedActor.uploaded_documents || []).length > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {(selectedActor.uploaded_documents || []).length}
                      </Badge>
                    )}
                  </Label>
                  {(selectedActor.uploaded_documents || []).length === 0 ? (
                    <p className="text-sm text-gray-500 mt-1 italic">
                      {t('social_actor_detail.basic_documents_empty', 'No documents have been uploaded yet.')}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {selectedActor.uploaded_documents.map((doc) => (
                        <a
                          key={doc.id || doc.file_url}
                          href={`${API}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 border rounded-md hover:bg-gray-50 hover:border-blue-400 transition-colors text-sm"
                          data-testid={`view-doc-${doc.id}`}
                        >
                          <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{doc.document_name || doc.filename}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {doc.document_type ? `${doc.document_type} · ` : ''}
                              {doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} KB` : ''}
                              {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString('en-GB')}` : ''}
                            </div>
                          </div>
                          <Download className="h-4 w-4 text-gray-500 shrink-0" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {canReview && selectedActor.status === 'pending_approval' && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => {
                        handleReviewActor(selectedActor.id, 'approve', 'level_1_commitment');
                        setShowDetailDialog(false);
                      }}
                      className="bg-green-700 hover:bg-green-800"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {t('social_actor_detail.basic_approve')}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        handleReviewActor(selectedActor.id, 'reject');
                        setShowDetailDialog(false);
                      }}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {t('social_actor_detail.basic_reject')}
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Certification & Scoring Tab */}
              <TabsContent value="scoring" className="space-y-6 mt-4">
                {/* Current Certification Status */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Award className="h-5 w-5 text-blue-600" />
                      {t('social_actor_detail.scoring_current_status_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      {getCertificationBadge(selectedActor.certification_level)}
                      <div className="text-sm text-gray-600">
                        {selectedActor.certification_date && (
                          <span>{t('social_actor_detail.scoring_certified_on', { date: new Date(selectedActor.certification_date).toLocaleDateString('en-GB') })}</span>
                        )}
                      </div>
                    </div>
                    {selectedActor.assessment_scores?.overall !== undefined && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{t('social_actor_detail.scoring_overall_score')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold text-blue-600">{selectedActor.assessment_scores.overall}%</span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => setShowBreakdown(true)}
                              data-testid="view-score-breakdown-btn"
                            >
                              <Eye className="h-3 w-3 mr-1" /> View Breakdown
                              {(user?.role === 'administrator' || user?.role === 'admin') ? ' & Override' : ''}
                            </Button>
                          </div>
                        </div>
                        <Progress value={selectedActor.assessment_scores.overall} className="h-3" />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Scoring Algorithm Breakdown */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-green-700" />
                      {t('social_actor_detail.scoring_algorithm_title')}
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('social_actor_detail.scoring_algorithm_subtitle')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">{t('social_actor_detail.scoring_col_category')}</th>
                            <th className="text-center p-3 font-semibold">{t('social_actor_detail.scoring_col_weight')}</th>
                            <th className="text-left p-3 font-semibold">{t('social_actor_detail.scoring_col_measures')}</th>
                            <th className="text-center p-3 font-semibold">{t('social_actor_detail.scoring_col_score')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                                {t('social_actor_detail.cat_strategy')}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">25%</Badge>
                            </td>
                            <td className="p-3 text-gray-600 text-xs">
                              {t('social_actor_detail.desc_strategy')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-blue-600">
                                {selectedActor.assessment_scores?.strategy ?? '—'}%
                              </span>
                            </td>
                          </tr>
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                {t('social_actor_detail.cat_governance')}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">25%</Badge>
                            </td>
                            <td className="p-3 text-gray-600 text-xs">
                              {t('social_actor_detail.desc_governance')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-purple-600">
                                {selectedActor.assessment_scores?.governance ?? '—'}%
                              </span>
                            </td>
                          </tr>
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-700"></div>
                                {t('social_actor_detail.cat_impact')}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">20%</Badge>
                            </td>
                            <td className="p-3 text-gray-600 text-xs">
                              {t('social_actor_detail.desc_impact')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-green-700">
                                {selectedActor.assessment_scores?.impact ?? '—'}%
                              </span>
                            </td>
                          </tr>
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                                {t('social_actor_detail.cat_transparency')}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">20%</Badge>
                            </td>
                            <td className="p-3 text-gray-600 text-xs">
                              {t('social_actor_detail.desc_transparency')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-orange-700">
                                {selectedActor.assessment_scores?.transparency ?? '—'}%
                              </span>
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                                {t('social_actor_detail.cat_investment_readiness')}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline">10%</Badge>
                            </td>
                            <td className="p-3 text-gray-600 text-xs">
                              {t('social_actor_detail.desc_investment')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-cyan-700">
                                {selectedActor.assessment_scores?.investment ?? '—'}%
                              </span>
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr className="bg-blue-50 font-semibold">
                            <td className="p-3" colSpan="2">
                              <span className="text-blue-800">{t('social_actor_detail.scoring_overall_weighted_label')}</span>
                            </td>
                            <td className="p-3 text-xs text-blue-600">
                              {t('social_actor_detail.scoring_overall_weighted_formula')}
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-xl font-bold text-blue-700">
                                {selectedActor.assessment_scores?.overall ?? '—'}%
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Certification Levels Legend */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-amber-600" />
                      {t('social_actor_detail.scoring_levels_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-lg border-2 ${selectedActor.assessment_scores?.overall >= 80 ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">🏆</span>
                          <span className="font-bold text-amber-700">{t('social_actor_detail.level_a_title')}</span>
                        </div>
                        <p className="text-sm font-medium">{t('social_actor_detail.level_a_label')}</p>
                        <p className="text-xs text-gray-600 mt-1">{t('social_actor_detail.level_a_score')}</p>
                        {selectedActor.assessment_scores?.overall >= 80 && (
                          <Badge className="mt-2 bg-amber-700">{t('social_actor_detail.current_level')}</Badge>
                        )}
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${selectedActor.assessment_scores?.overall >= 60 && selectedActor.assessment_scores?.overall < 80 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">⭐</span>
                          <span className="font-bold text-blue-700">{t('social_actor_detail.level_b_title')}</span>
                        </div>
                        <p className="text-sm font-medium">{t('social_actor_detail.level_b_label')}</p>
                        <p className="text-xs text-gray-600 mt-1">{t('social_actor_detail.level_b_score')}</p>
                        {selectedActor.assessment_scores?.overall >= 60 && selectedActor.assessment_scores?.overall < 80 && (
                          <Badge className="mt-2 bg-blue-500">{t('social_actor_detail.current_level')}</Badge>
                        )}
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${selectedActor.assessment_scores?.overall >= 40 && selectedActor.assessment_scores?.overall < 60 ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">✓</span>
                          <span className="font-bold text-green-700">{t('social_actor_detail.level_c_title')}</span>
                        </div>
                        <p className="text-sm font-medium">{t('social_actor_detail.level_c_label')}</p>
                        <p className="text-xs text-gray-600 mt-1">{t('social_actor_detail.level_c_score')}</p>
                        {selectedActor.assessment_scores?.overall >= 40 && selectedActor.assessment_scores?.overall < 60 && (
                          <Badge className="mt-2 bg-green-700">{t('social_actor_detail.current_level')}</Badge>
                        )}
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${!selectedActor.assessment_scores?.overall || selectedActor.assessment_scores?.overall < 40 ? 'border-gray-500 bg-gray-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">○</span>
                          <span className="font-bold text-gray-700">{t('social_actor_detail.level_not_cert_title')}</span>
                        </div>
                        <p className="text-sm font-medium">{t('social_actor_detail.level_not_cert_label')}</p>
                        <p className="text-xs text-gray-600 mt-1">{t('social_actor_detail.level_not_cert_score')}</p>
                        {(!selectedActor.assessment_scores?.overall || selectedActor.assessment_scores?.overall < 40) && (
                          <Badge className="mt-2 bg-gray-500">{t('social_actor_detail.current_level')}</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Calculate/Recalculate Score Button - Only for PM/Admin */}
                {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{t('social_actor_detail.recalc_title')}</h4>
                          <p className="text-sm text-gray-500">
                            {t('social_actor_detail.recalc_subtitle')}
                          </p>
                          {selectedActor.last_assessment_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              {t('social_actor_detail.recalc_last_calculated', { date: new Date(selectedActor.last_assessment_date).toLocaleString('en-GB') })}
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={async () => {
                            try {
                              const response = await axios.post(
                                `${API}/api/social-actors/${selectedActor.id}/calculate-score`,
                                {},
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                              );
                              toast.success(t('social_actor_detail.recalc_success', { score: response.data.scores.overall }));
                              // Refresh the actor data
                              const updatedActor = await axios.get(
                                `${API}/api/social-actors/${selectedActor.id}`,
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                              );
                              setSelectedActor(updatedActor.data);
                              fetchActors();
                            } catch (error) {
                              console.error('Failed to calculate score:', error);
                              toast.error(t('social_actor_detail.recalc_failed'));
                            }
                          }}
                        >
                          <BarChart3 className="h-4 w-4 mr-2" />
                          {t('social_actor_detail.recalc_btn')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="nearby" className="mt-4">
                <NearbyOrganizations
                  actorId={selectedActor.id}
                  actorName={selectedActor.organization_name}
                  onViewDetails={(nearbyOrg) => {
                    // Close current dialog and open the nearby org's details
                    setShowDetailDialog(false);
                    viewActorDetails(nearbyOrg.id);
                  }}
                />
              </TabsContent>

              <TabsContent value="collaborations" className="mt-4">
                <CollaborationRequests
                  onViewOrganization={(orgId) => {
                    setShowDetailDialog(false);
                    viewActorDetails(orgId);
                  }}
                />
              </TabsContent>

              <TabsContent value="assessment" className="mt-4">
                <SocialActorAssessment
                  actor={selectedActor}
                  currentUser={user}
                  onActorRefreshed={async () => {
                    try {
                      const fresh = await axios.get(
                        `${API}/api/social-actors/${selectedActor.id}`,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                      );
                      setSelectedActor(fresh.data);
                      await fetchActors();
                    } catch (e) {
                      console.error('Failed to refresh actor:', e);
                    }
                  }}
                  onUpdate={async (assessmentData) => {
                    try {
                      const response = await axios.patch(
                        `${API}/api/social-actors/${selectedActor.id}`,
                        assessmentData,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                      );
                      setSelectedActor(response.data);
                      await fetchActors();
                    } catch (error) {
                      console.error('Failed to update assessment:', error);
                      throw error;
                    }
                  }}
                  canEdit={canEdit(selectedActor)}
                />
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Comparison Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              {t('social_actor_registry.compare_dialog_title', { count: selectedForComparison.length })}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-3 text-left font-semibold border-b w-40">{t('social_actor_registry.compare_col_attribute')}</th>
                  {selectedForComparison.map(actor => (
                    <th key={actor.id} className="p-3 text-left font-semibold border-b">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-500" />
                        {actor.organization_name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_type')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3 capitalize">
                      {actor.organization_type?.replace('_', ' ')}
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_location')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.city}, {actor.country}
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_certification')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {getCertificationBadge(actor.certification_level)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_overall')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      <span className="text-lg font-bold text-blue-600">
                        {actor.assessment_scores?.overall ?? '—'}%
                      </span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_strategy')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.assessment_scores?.strategy ?? '—'}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_governance')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.assessment_scores?.governance ?? '—'}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_impact')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.assessment_scores?.impact ?? '—'}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_transparency')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.assessment_scores?.transparency ?? '—'}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_investment')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.assessment_scores?.investment ?? '—'}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_sdg')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {actor.sdg_alignment?.map(sdg => (
                          <Badge key={sdg} variant="outline" className="text-xs">
                            SDG {sdg}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_year')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.year_established || '—'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_legal_form')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      {actor.legal_form || '—'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_completeness')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3">
                      <div className="flex items-center gap-2">
                        <Progress value={calculateCompleteness(actor).percentage} className="h-2 w-20" />
                        <span className="text-sm font-medium">{calculateCompleteness(actor).percentage}%</span>
                      </div>
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-600">{t('social_actor_registry.compare_row_mission')}</td>
                  {selectedForComparison.map(actor => (
                    <td key={actor.id} className="p-3 text-sm">
                      {actor.social_mission?.substring(0, 150)}
                      {actor.social_mission?.length > 150 ? '...' : ''}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => {
              setShowCompareDialog(false);
              exitCompareMode();
            }}>
              {t('social_actor_registry.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity History Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Activity History {selectedActor && `- ${selectedActor.organization_name}`}
            </DialogTitle>
          </DialogHeader>
          
          {loadingActivity ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : activityHistory.length > 0 ? (
            <div className="space-y-4">
              {activityHistory.map((activity, idx) => (
                <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activity.action === 'created' ? 'bg-green-100 text-green-700' :
                      activity.action === 'updated' ? 'bg-blue-100 text-blue-600' :
                      activity.action === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      activity.action === 'rejected' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {activity.action === 'created' && <Plus className="h-5 w-5" />}
                      {activity.action === 'updated' && <Edit className="h-5 w-5" />}
                      {activity.action === 'approved' && <CheckCircle className="h-5 w-5" />}
                      {activity.action === 'rejected' && <XCircle className="h-5 w-5" />}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{activity.action}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleString('en-GB')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      by <span className="font-medium">{activity.user}</span>
                    </p>
                    {activity.changes && (
                      <div className="mt-2 text-xs text-gray-500">
                        Changed: {Object.keys(activity.changes).join(', ')}
                      </div>
                    )}
                    {activity.notes && (
                      <p className="mt-2 text-sm text-gray-700 italic">&ldquo;{activity.notes}&rdquo;</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No activity history available</p>
              <p className="text-sm text-gray-500 mt-1">
                Activity will be recorded when profile changes are made
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <SocialActorQRCode
        actor={qrCodeActor}
        isOpen={showQRCode}
        onClose={() => {
          setShowQRCode(false);
          setQRCodeActor(null);
        }}
      />
      
      {/* CSV Import Dialog */}
      <CSVImportDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={() => {
          fetchActors();
          setShowImportDialog(false);
        }}
      />

      {/* Score Breakdown & Override Modal */}
      {selectedActor && (
        <ScoreBreakdownModal
          open={showBreakdown}
          onClose={() => setShowBreakdown(false)}
          entityType="actor"
          entityId={selectedActor.id}
          entityName={selectedActor.organization_name}
          isAdmin={user?.role === 'administrator' || user?.role === 'admin'}
          onChanged={() => {
            // Refresh actor detail to reflect new effective scores
            if (selectedActor?.id) {
              axios.get(`${BACKEND_URL}/api/social-actors/${selectedActor.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              }).then(res => setSelectedActor(res.data)).catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
};

export default SocialActorRegistry;
